import { useEffect, useMemo, useState } from "react";
import {
  Edit3,
  KeyRound,
  Mail,
  Plus,
  Search,
  ShieldCheck,
  UserRound,
  UsersRound
} from "lucide-react";
import type { AccountAccessAssignment, AccountPermissions, Role, User } from "../types";
import { api } from "../lib/api";
import { Badge, Button, EmptyState, IconButton, Loading, Modal, Switch } from "../components/ui";
import UserAvatar from "../components/UserAvatar";

type UserForm = {
  employeeId: string;
  firstName: string;
  lastName: string;
  username: string;
  isAdministrator: boolean;
  isActive: boolean;
  roleIds: string[];
};

const blankUser: UserForm = {
  employeeId: "",
  firstName: "",
  lastName: "",
  username: "",
  isAdministrator: false,
  isActive: true,
  roleIds: []
};

export default function TeamPage({
  mode,
  notify
}: {
  mode: "users" | "roles";
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState<User | null | "new">(null);
  const [userForm, setUserForm] = useState<UserForm>(blankUser);
  const [editingRole, setEditingRole] = useState<Role | null | "new">(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "", isActive: true });
  const [saving, setSaving] = useState(false);
  const [accessTarget, setAccessTarget] = useState<{ kind: "user" | "role"; id: string; name: string }>();
  const [accessRows, setAccessRows] = useState<AccountAccessAssignment[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File>();

  const load = async () => {
    setLoading(true);
    try {
      const [userResult, roleResult] = await Promise.all([api.users(), api.roles()]);
      setUsers(userResult);
      setRoles(roleResult);
    } catch (error) {
      notify("error", "Couldn’t load team access", (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredUsers = useMemo(
    () =>
      users.filter((user) =>
        `${user.firstName} ${user.lastName} ${user.employeeId} ${user.username} ${user.roles.join(" ")}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [search, users]
  );
  const filteredRoles = useMemo(
    () =>
      roles.filter((role) =>
        `${role.name} ${role.description}`.toLowerCase().includes(search.toLowerCase())
      ),
    [roles, search]
  );

  const openAccess = async (kind: "user" | "role", id: string, name: string) => {
    setAccessTarget({ kind, id, name });
    setAccessLoading(true);
    try {
      setAccessRows(kind === "user" ? await api.userAccountAccess(id) : await api.roleAccountAccess(id));
    } catch (error) {
      notify("error", "Couldn’t load mailbox access", (error as Error).message);
      setAccessTarget(undefined);
    } finally {
      setAccessLoading(false);
    }
  };

  const saveAccess = async (row: AccountAccessAssignment, permissions?: AccountPermissions) => {
    if (!accessTarget) return;
    const next = permissions ?? row.permissions;
    try {
      if (accessTarget.kind === "user") await api.setUserAccountAccess(accessTarget.id, row.accountId, next);
      else await api.setRoleAccountAccess(accessTarget.id, row.accountId, next);
      setAccessRows((current) => current.map((item) => item.accountId === row.accountId
        ? { ...item, isAssigned: true, permissions: next } : item));
    } catch (error) { notify("error", "Couldn’t update access", (error as Error).message); }
  };

  const removeAccess = async (row: AccountAccessAssignment) => {
    if (!accessTarget) return;
    try {
      if (accessTarget.kind === "user") await api.removeUserAccountAccess(accessTarget.id, row.accountId);
      else await api.removeRoleAccountAccess(accessTarget.id, row.accountId);
      setAccessRows((current) => current.map((item) => item.accountId === row.accountId
        ? { ...item, isAssigned: false } : item));
    } catch (error) { notify("error", "Couldn’t remove access", (error as Error).message); }
  };

  const openUser = (user?: User) => {
    setPhotoFile(undefined);
    if (!user) {
      setEditingUser("new");
      setUserForm(blankUser);
      return;
    }
    setEditingUser(user);
    setUserForm({
      employeeId: user.employeeId,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      isAdministrator: user.isAdministrator,
      isActive: user.isActive,
      roleIds: roles.filter((role) => user.roles.includes(role.name)).map((role) => role.id)
    });
  };

  const saveUser = async () => {
    setSaving(true);
    try {
      let savedId: string | undefined;
      if (editingUser === "new") {
        const created = await api.createUser(userForm);
        savedId = created.id;
        notify(
          "success",
          "User created",
          "Their temporary password is the employee ID; a password change is required."
        );
      } else if (editingUser) {
        const updated = await api.updateUser(editingUser.id, userForm);
        savedId = updated.id;
        notify("success", "User updated");
      }
      if (photoFile && savedId) await api.uploadUserPhoto(savedId, photoFile);
      setEditingUser(null);
      await load();
    } catch (error) {
      notify("error", "Couldn’t save user", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (user: User) => {
    if (!window.confirm(`Reset ${user.firstName}’s password to ${user.employeeId}?`)) return;
    try {
      await api.resetPassword(user.id);
      notify(
        "success",
        "Password reset",
        "The user must change it after signing in with their employee ID."
      );
      await load();
    } catch (error) {
      notify("error", "Couldn’t reset password", (error as Error).message);
    }
  };

  const openRole = (role?: Role) => {
    if (!role) {
      setEditingRole("new");
      setRoleForm({ name: "", description: "", isActive: true });
      return;
    }
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description, isActive: role.isActive });
  };

  const saveRole = async () => {
    setSaving(true);
    try {
      if (editingRole === "new") {
        await api.createRole(roleForm);
        notify("success", "Role created");
      } else if (editingRole) {
        await api.updateRole(editingRole.id, roleForm);
        notify("success", "Role updated");
      }
      setEditingRole(null);
      await load();
    } catch (error) {
      notify("error", "Couldn’t save role", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page team-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Role-based access control</span>
          <h1>{mode === "users" ? "People" : "Roles & permissions"}</h1>
          <p>
            {mode === "users"
              ? "Manage employee access, first-login security, and role membership."
              : "Group access by job function so shared mailboxes stay easy to manage."}
          </p>
        </div>
        <Button
          icon={<Plus size={17} />}
          onClick={() => (mode === "users" ? openUser() : openRole())}
        >
          {mode === "users" ? "Add user" : "New role"}
        </Button>
      </div>

      <div className="content-toolbar">
        <label className="search-field">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={mode === "users" ? "Search people" : "Search roles"}
          />
        </label>
      </div>

      {loading ? (
        <Loading label="Loading access settings…" />
      ) : mode === "users" ? (
        filteredUsers.length === 0 ? (
          <EmptyState icon={<UserRound />} title="No matching people" message="Try a different search." />
        ) : (
          <section className="data-panel">
            <div className="data-table user-table">
              <div className="data-row data-head">
                <span>Person</span>
                <span>Employee ID</span>
                <span>Access</span>
                <span>Status</span>
                <span />
              </div>
              {filteredUsers.map((user, index) => (
                <div className="data-row" key={user.id}>
                  <span className="person-cell">
                    <UserAvatar user={user} tone={index} />
                    <span>
                      <strong>
                        {user.firstName} {user.lastName}
                      </strong>
                      <small>@{user.username}</small>
                    </span>
                  </span>
                  <span className="mono-cell">{user.employeeId}</span>
                  <span className="role-tags">
                    {user.isAdministrator && <Badge tone="violet">Administrator</Badge>}
                    {user.roles.map((role) => (
                      <Badge key={role}>{role}</Badge>
                    ))}
                    {!user.isAdministrator && user.roles.length === 0 && <small>Direct access only</small>}
                  </span>
                  <span>
                    <Badge tone={user.isActive ? "success" : "warning"}>
                      {user.isActive ? "Active" : "Disabled"}
                    </Badge>
                    {user.mustChangePassword && <Badge tone="info">Password change</Badge>}
                  </span>
                  <span className="row-actions">
                    <IconButton
                      label="Mailbox access"
                      onClick={() => openAccess("user", user.id, `${user.firstName} ${user.lastName}`)}
                    >
                      <ShieldCheck size={16} />
                    </IconButton>
                    <IconButton label="Reset password" onClick={() => resetPassword(user)}>
                      <KeyRound size={16} />
                    </IconButton>
                    <IconButton label="Edit user" onClick={() => openUser(user)}>
                      <Edit3 size={16} />
                    </IconButton>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )
      ) : filteredRoles.length === 0 ? (
        <EmptyState icon={<UsersRound />} title="No matching roles" message="Try a different search." />
      ) : (
        <div className="role-grid">
          {filteredRoles.map((role, index) => (
            <article className="role-card" key={role.id}>
              <header>
                <span className={`role-icon role-tone-${index % 4}`}>
                  <ShieldCheck size={19} />
                </span>
                <Badge tone={role.isActive ? "success" : "warning"}>
                  {role.isActive ? "Active" : "Disabled"}
                </Badge>
                <IconButton label="Mailbox access" onClick={() => openAccess("role", role.id, role.name)}>
                  <Mail size={16} />
                </IconButton>
                <IconButton label="Edit role" onClick={() => openRole(role)}>
                  <Edit3 size={16} />
                </IconButton>
              </header>
              <h2>{role.name}</h2>
              <p>{role.description || "No role description yet."}</p>
              <footer>
                <span>
                  <strong>{role.memberCount}</strong> members
                </span>
                <span>
                  <strong>{role.accountCount}</strong> mailboxes
                </span>
              </footer>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={editingUser !== null}
        onClose={() => setEditingUser(null)}
        title={editingUser === "new" ? "Add user" : "Edit user"}
        subtitle="New users sign in with their employee ID and must immediately change it."
      >
        <div className="stack-form">
          <div className="form-grid two">
            <label className="field">
              <span>First name</span>
              <input
                value={userForm.firstName}
                onChange={(event) => setUserForm({ ...userForm, firstName: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Last name</span>
              <input
                value={userForm.lastName}
                onChange={(event) => setUserForm({ ...userForm, lastName: event.target.value })}
              />
            </label>
          </div>
          <div className="form-grid two">
            <label className="field">
              <span>Employee ID</span>
              <input
                value={userForm.employeeId}
                onChange={(event) => setUserForm({ ...userForm, employeeId: event.target.value })}
                placeholder="EMP1023"
              />
            </label>
            <label className="field">
              <span>Username</span>
              <input
                value={userForm.username}
                onChange={(event) => setUserForm({ ...userForm, username: event.target.value })}
                placeholder="mary"
              />
            </label>
          </div>
          <label className="field">
            <span>Profile photo (optional)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => setPhotoFile(event.target.files?.[0])}
            />
          </label>
          <fieldset className="role-checklist">
            <legend>Roles</legend>
            {roles.map((role) => (
              <label key={role.id}>
                <input
                  type="checkbox"
                  checked={userForm.roleIds.includes(role.id)}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      roleIds: event.target.checked
                        ? [...userForm.roleIds, role.id]
                        : userForm.roleIds.filter((id) => id !== role.id)
                    })
                  }
                />
                <span>
                  <strong>{role.name}</strong>
                  <small>{role.description}</small>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="switch-row">
            <div>
              <strong>Administrator</strong>
              <span>Full visibility and system management access.</span>
            </div>
            <Switch
              checked={userForm.isAdministrator}
              onChange={(isAdministrator) => setUserForm({ ...userForm, isAdministrator })}
              label="Administrator"
            />
          </div>
          <div className="switch-row">
            <div>
              <strong>Account active</strong>
              <span>Disabled users cannot sign in.</span>
            </div>
            <Switch
              checked={userForm.isActive}
              onChange={(isActive) => setUserForm({ ...userForm, isActive })}
              label="User active"
            />
          </div>
        </div>
        <div className="modal-footer">
          <Button tone="secondary" onClick={() => setEditingUser(null)}>
            Cancel
          </Button>
          <Button onClick={saveUser} disabled={saving}>
            {saving ? "Saving…" : editingUser === "new" ? "Create user" : "Save changes"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={editingRole !== null}
        onClose={() => setEditingRole(null)}
        title={editingRole === "new" ? "Create role" : "Edit role"}
        subtitle="Roles bundle mailbox access for everyone who belongs to them."
      >
        <div className="stack-form">
          <label className="field">
            <span>Role name</span>
            <input
              value={roleForm.name}
              onChange={(event) => setRoleForm({ ...roleForm, name: event.target.value })}
              placeholder="Reservations"
            />
          </label>
          <label className="field">
            <span>Description</span>
            <textarea
              rows={4}
              value={roleForm.description}
              onChange={(event) => setRoleForm({ ...roleForm, description: event.target.value })}
              placeholder="What this role is responsible for"
            />
          </label>
          <div className="switch-row">
            <div>
              <strong>Role active</strong>
              <span>Disabled roles stop granting access.</span>
            </div>
            <Switch
              checked={roleForm.isActive}
              onChange={(isActive) => setRoleForm({ ...roleForm, isActive })}
              label="Role active"
            />
          </div>
        </div>
        <div className="modal-footer">
          <Button tone="secondary" onClick={() => setEditingRole(null)}>
            Cancel
          </Button>
          <Button onClick={saveRole} disabled={saving}>
            {saving ? "Saving…" : editingRole === "new" ? "Create role" : "Save changes"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(accessTarget)}
        onClose={() => setAccessTarget(undefined)}
        title={`Mailbox access · ${accessTarget?.name ?? ""}`}
        subtitle="Assign mailboxes and control each action. Role and direct grants are combined."
        wide
      >
        {accessLoading ? <Loading label="Loading mailbox access…" /> : (
          <div className="stack-form access-matrix">
            {accessRows.map((row) => (
              <section className="form-section" key={row.accountId}>
                <div className="switch-row">
                  <div><strong>{row.displayName}</strong><span>{row.emailAddress}</span></div>
                  <Switch
                    checked={row.isAssigned}
                    onChange={(assigned) => assigned ? saveAccess(row) : removeAccess(row)}
                    label={`Assign ${row.displayName}`}
                  />
                </div>
                {row.isAssigned && (
                  <div className="role-checklist permission-grid">
                    {([
                      ["canRead", "Read"], ["canSend", "Send"], ["canReply", "Reply"],
                      ["canReplyAll", "Reply all"], ["canForward", "Forward"], ["canDelete", "Delete"],
                      ["canUseTemplates", "Use templates"], ["canViewAttachments", "View attachments"],
                      ["canDownloadAttachments", "Download attachments"], ["canViewSent", "View sent"],
                      ["canViewArchive", "View archive"]
                    ] as Array<[keyof AccountPermissions, string]>).map(([key, label]) => (
                      <label key={key}>
                        <input
                          type="checkbox"
                          checked={row.permissions[key]}
                          onChange={(event) => saveAccess(row, { ...row.permissions, [key]: event.target.checked })}
                        />
                        <span><strong>{label}</strong></span>
                      </label>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <Button onClick={() => setAccessTarget(undefined)}>Done</Button>
        </div>
      </Modal>    </div>
  );
}


